import { useState, useEffect, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar as CalendarComponent } from "@/components/ui/calendar";
import { Search, MapPin, Calendar, Heart, ChevronRight, BarChart3, Plus, Menu, Users, SlidersHorizontal, Grid2X2, Home, Star } from "lucide-react";
import { ApartmentIcon } from "@/components/icons/PropertyTypeIcons";
import { format } from "date-fns";
import { getDateLocale } from "@/lib/dateUtils";
import { useAuth } from "@/contexts/AuthContext";
import { useUserRoles } from "@/hooks/useUserRoles";
import MobileUserDrawer from "@/components/layout/MobileUserDrawer";
import NotificationBell from "@/components/notifications/NotificationBell";
import { motion, AnimatePresence } from "framer-motion";
import { supabase } from "@/integrations/supabase/client";
import VerifiedBadge from "@/components/VerifiedBadge";
import { Badge } from "@/components/ui/badge";
import OptimizedImage from "@/components/ui/OptimizedImage";
import { getFirstImageUrl, formatKz } from "@/lib/utils";
import { formatMoney } from "@/lib/money";
import { useUserCurrency } from "@/hooks/useUserCurrency";
import { useTranslation } from "react-i18next";

interface Property {
  id: string;
  title: string;
  city: string;
  province: string;
  country: string;
  property_type: string;
  price_per_night: number;
  currency: string | null;
  images: string[] | null;
  average_rating: number | null;
  total_reviews: number | null;
  is_verified: boolean | null;
  bedrooms: number | null;
  bathrooms: number | null;
  max_guests: number | null;
}

const MobileHomeView = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { currency: userCurrency } = useUserCurrency();
  const fmt = (v: number) => (userCurrency === "AOA" ? formatKz(v) : formatMoney(v, userCurrency));
  const { user, signOut, loading: authLoading } = useAuth();
  const { isHost, isGuest, isSuperAdmin } = useUserRoles();
  const isHostOnly = isHost && !isGuest && !isSuperAdmin;
  const [searchQuery, setSearchQuery] = useState("");
  const [dateRange, setDateRange] = useState<{ from: Date | undefined; to: Date | undefined }>({
    from: undefined,
    to: undefined,
  });
  const [isDateOpen, setIsDateOpen] = useState(false);
  const [properties, setProperties] = useState<Property[]>([]);
  const [loading, setLoading] = useState(true);
  const [isKeyboardOpen, setIsKeyboardOpen] = useState(false);
  const [hostPropertyCount, setHostPropertyCount] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const mainRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const handleResize = () => {
      if (window.visualViewport) {
        const isOpen = window.visualViewport.height < window.innerHeight * 0.75;
        setIsKeyboardOpen(isOpen);
      }
    };
    const handleFocusIn = () => {
      setIsKeyboardOpen(true);
      setTimeout(() => {
        inputRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 100);
    };
    const handleFocusOut = () => {
      setTimeout(() => setIsKeyboardOpen(false), 100);
    };

    window.visualViewport?.addEventListener('resize', handleResize);
    document.addEventListener('focusin', handleFocusIn);
    document.addEventListener('focusout', handleFocusOut);

    return () => {
      window.visualViewport?.removeEventListener('resize', handleResize);
      document.removeEventListener('focusin', handleFocusIn);
      document.removeEventListener('focusout', handleFocusOut);
    };
  }, []);

  useEffect(() => {
    const fetchProperties = async () => {
      const { data } = await supabase
        .from("properties")
        .select("id, title, city, province, country, property_type, price_per_night, currency, images, average_rating, total_reviews, is_verified, owner_id, bedrooms, bathrooms, max_guests")
        .eq("status", "active")
        .limit(10);
      
      if (data) {
        setProperties(data as any);
        if (isHostOnly && user) {
          setHostPropertyCount(data.filter(p => (p as any).owner_id === user.id).length);
        }
      }
      setLoading(false);
    };
    fetchProperties();
  }, [isHostOnly, user]);

  const handleSearch = () => {
    const params = new URLSearchParams();
    if (searchQuery) params.set("search", searchQuery);
    if (dateRange.from) params.set("checkIn", dateRange.from.toISOString());
    if (dateRange.to) params.set("checkOut", dateRange.to.toISOString());
    
    navigate(`/explorar?${params.toString()}`);
  };

  const dateLabel = dateRange.from 
    ? dateRange.to 
      ? `${format(dateRange.from, "dd/MM", { locale: getDateLocale() })} - ${format(dateRange.to, "dd/MM", { locale: getDateLocale() })}`
      : format(dateRange.from, "dd/MM", { locale: getDateLocale() })
    : t('mobileHome.addDates') || "Adicionar datas";

  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const getInitials = () => {
    if (user?.user_metadata?.full_name) {
      return user.user_metadata.full_name.split(" ").map((n: string) => n[0]).join("").toUpperCase().slice(0, 2);
    }
    return user?.email?.slice(0, 2).toUpperCase() || "U";
  };

  const handleSignOut = async () => {
    await signOut();
  };

  return (
    <div className="h-[100dvh] bg-[#F8F9FA] dark:bg-[#121212] flex flex-col overflow-hidden pb-14">
      {/* Floating Menu Trigger — replaces top header bar */}
      <div className="flex-shrink-0 px-5 pt-[max(env(safe-area-inset-top),20px)] pb-4 flex items-center justify-between z-10 bg-[#F8F9FA] dark:bg-[#121212]">
        {/* Notification bell */}
        <div className="relative">
          <NotificationBell />
        </div>
        
        {/* Menu button */}
        <button
          onClick={() => setIsMenuOpen(true)}
          className="w-12 h-12 rounded-full bg-white dark:bg-[#1C1C1E] shadow-sm dark:shadow-none flex items-center justify-center border border-zinc-100 dark:border-[#2A2A2A] active:scale-95 transition-all"
          aria-label="Abrir menu"
        >
          <Menu className="w-5 h-5 text-[#E8513D]" strokeWidth={2.5} />
        </button>
      </div>

      {/* Mobile Drawer */}
      <MobileUserDrawer
        open={isMenuOpen}
        onOpenChange={setIsMenuOpen}
        user={user}
        loading={authLoading}
        getInitials={getInitials}
        onSignOut={handleSignOut}
      />

      <main 
        ref={mainRef}
        className={`flex-1 flex flex-col px-5 overflow-y-auto pb-6`}
      >
        {isHostOnly ? (
          <div className="bg-white rounded-3xl p-5 shadow-sm border border-zinc-100 mb-6">
            <div className="flex items-center justify-between gap-3 mb-4">
              <div className="flex items-center gap-3">
                <Badge variant="secondary" className="gap-1.5 text-xs px-3 py-1.5 rounded-full bg-zinc-100 text-zinc-800">
                  <BarChart3 className="w-3.5 h-3.5 text-[#E8513D]" />
                  {hostPropertyCount} {t('mobileHome.activeSpaces') || "espaços ativos"}
                </Badge>
              </div>
            </div>
            <div className="flex flex-col gap-3">
              <Button
                className="w-full h-12 rounded-2xl bg-[#E8513D] hover:bg-[#D14430] text-white font-bold text-[15px]"
                onClick={() => navigate("/anunciar")}
              >
                <Plus className="w-4 h-4 mr-2" /> {t('mobileHome.advertiseSpace') || "Anunciar Novo Espaço"}
              </Button>
              <Button
                variant="outline"
                className="w-full h-12 rounded-2xl border-zinc-200 text-zinc-700 font-semibold text-[15px]"
                onClick={() => navigate("/dashboard")}
              >
                {t('mobileHome.openDashboard') || "Abrir Painel"}
              </Button>
            </div>
          </div>
        ) : (
          <>
            {/* Search Card block */}
            <div className="bg-white dark:bg-[#1C1C1E] rounded-[24px] shadow-sm dark:shadow-none border border-zinc-100 dark:border-[#2A2A2A] mb-6 flex flex-col overflow-hidden">
              {/* Quando */}
              <Popover open={isDateOpen} onOpenChange={setIsDateOpen}>
                <PopoverTrigger asChild>
                  <button className="flex items-center justify-between p-4 text-left hover:bg-zinc-50 dark:hover:bg-white/5 transition-colors">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 rounded-full border border-[#E8513D]/20 bg-[#E8513D]/5 dark:bg-[#E8513D]/10 flex items-center justify-center shrink-0">
                        <Calendar className="w-5 h-5 text-[#E8513D]" strokeWidth={2} />
                      </div>
                      <div className="flex flex-col">
                        <span className="text-[10px] font-bold text-[#E8513D] uppercase tracking-wider mb-0.5">{t('mobileHome.when') || "Quando?"}</span>
                        <span className={dateRange.from ? "text-[15px] font-bold text-zinc-900 dark:text-zinc-100" : "text-[15px] font-bold text-zinc-900 dark:text-zinc-100"}>
                          {dateLabel}
                        </span>
                      </div>
                    </div>
                    <ChevronRight className="w-5 h-5 text-zinc-400 dark:text-zinc-500 rotate-90" />
                  </button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="center">
                  <CalendarComponent
                    mode="range"
                    selected={dateRange}
                    onSelect={(range) => {
                      setDateRange({ from: range?.from, to: range?.to });
                      if (range?.to) setIsDateOpen(false);
                    }}
                    numberOfMonths={1}
                    disabled={(date) => date < new Date()}
                    locale={getDateLocale()}
                    className="pointer-events-auto"
                  />
                </PopoverContent>
              </Popover>

              <div className="h-[1px] bg-gradient-to-r from-transparent via-[#DCD6CC]/80 dark:via-[#DCD6CC]/30 to-transparent mx-4" />

              {/* Hospedes */}
              <button className="flex items-center justify-between p-4 text-left hover:bg-zinc-50 dark:hover:bg-white/5 transition-colors">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-full border border-[#E8513D]/20 bg-[#E8513D]/5 dark:bg-[#E8513D]/10 flex items-center justify-center shrink-0">
                    <Users className="w-5 h-5 text-[#E8513D]" strokeWidth={2} />
                  </div>
                  <div className="flex flex-col">
                    <span className="text-[10px] font-bold text-[#E8513D] uppercase tracking-wider mb-0.5">{t('mobileHome.guests') || "Hóspedes"}</span>
                    <span className="text-[15px] font-bold text-zinc-900 dark:text-zinc-100">{t('mobileHome.guestCount', { count: 2 }) || "2 hóspedes"}</span>
                  </div>
                </div>
                <ChevronRight className="w-5 h-5 text-zinc-400 dark:text-zinc-500 rotate-90" />
              </button>

              <div className="h-[1px] bg-gradient-to-r from-transparent via-[#DCD6CC]/80 dark:via-[#DCD6CC]/30 to-transparent mx-4" />

              {/* Local */}
              <div className="flex items-center justify-between p-4 bg-white dark:bg-[#1C1C1E] relative group">
                <div className="flex items-center gap-4 flex-1">
                  <div className="w-12 h-12 rounded-full border border-[#E8513D]/20 bg-[#E8513D]/5 dark:bg-[#E8513D]/10 flex items-center justify-center shrink-0">
                    <MapPin className="w-5 h-5 text-[#E8513D]" strokeWidth={2} />
                  </div>
                  <div className="flex flex-col flex-1">
                    <Input
                      ref={inputRef}
                      type="text"
                      placeholder={t('mobileHome.searchPlaceholder') || "Endereço, bairro ou local"}
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="border-0 bg-transparent p-0 h-auto text-[15px] text-zinc-900 dark:text-zinc-100 placeholder:text-zinc-400 focus-visible:ring-0 focus-visible:ring-offset-0 font-medium"
                      enterKeyHint="search"
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          handleSearch();
                          inputRef.current?.blur();
                        }
                      }}
                    />
                  </div>
                </div>
                <button 
                  onClick={handleSearch}
                  className="w-12 h-12 rounded-full bg-[#E8513D] text-white flex items-center justify-center shadow-md shadow-[#E8513D]/20 active:scale-95 transition-transform shrink-0 ml-2"
                >
                  <Search className="w-5 h-5" strokeWidth={2.5} />
                </button>
              </div>
            </div>
          </>
        )}

        {/* Categories / Filters row */}
        {!isHostOnly && (
          <div className="flex items-center gap-3 overflow-x-auto pb-4 mb-2 no-scrollbar -mx-5 px-5">
            <button className="flex items-center gap-2 px-5 py-2.5 bg-white dark:bg-[#1C1C1E] border border-zinc-200 dark:border-[#2A2A2A] rounded-full shrink-0 shadow-sm dark:shadow-none active:scale-95 transition-transform">
              <SlidersHorizontal className="w-4 h-4 text-[#E8513D]" />
              <span className="font-semibold text-zinc-800 dark:text-zinc-200 text-[14px]">{t('mobileHome.filters') || "Filtros"}</span>
            </button>
            <button className="flex items-center gap-2 px-5 py-2.5 bg-white dark:bg-[#1C1C1E] border border-zinc-200 dark:border-[#2A2A2A] rounded-full shrink-0 shadow-sm dark:shadow-none active:scale-95 transition-transform">
              <Grid2X2 className="w-4 h-4 text-[#E8513D]" />
              <span className="font-semibold text-zinc-800 dark:text-zinc-200 text-[14px]">{t('mobileHome.all') || "Todos"}</span>
            </button>
            <button className="flex items-center gap-2 px-5 py-2.5 bg-white dark:bg-[#1C1C1E] border border-zinc-200 dark:border-[#2A2A2A] rounded-full shrink-0 shadow-sm dark:shadow-none active:scale-95 transition-transform">
              <ApartmentIcon className="w-4 h-4 text-[#E8513D]" />
              <span className="font-semibold text-zinc-800 dark:text-zinc-200 text-[14px]">{t('propertyTypes.Apartamento.label') || "Apartamento"}</span>
            </button>
            <button className="w-10 h-10 rounded-full bg-white dark:bg-[#1C1C1E] border border-zinc-200 dark:border-[#2A2A2A] flex items-center justify-center shrink-0 shadow-sm dark:shadow-none active:scale-95 transition-transform">
              <ChevronRight className="w-5 h-5 text-zinc-600 dark:text-zinc-400" />
            </button>
          </div>
        )}

        {/* Properties Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 min-h-0 pb-8">
          {loading ? (
            Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="bg-white rounded-3xl overflow-hidden animate-pulse shadow-sm border border-zinc-100">
                <div className="aspect-[4/3] bg-zinc-200" />
                <div className="p-4 space-y-3">
                  <div className="h-4 bg-zinc-200 rounded w-2/3" />
                  <div className="h-4 bg-zinc-200 rounded w-1/2" />
                </div>
              </div>
            ))
          ) : (
            properties.map((property) => (
              <Link 
                key={property.id} 
                to={`/imovel/${property.id}`}
                className="group relative bg-white rounded-3xl shadow-sm border border-zinc-100 overflow-hidden active:scale-[0.98] transition-all duration-200"
              >
                {/* Image */}
                <div className="relative aspect-[4/3] w-full bg-zinc-100 overflow-hidden">
                  <OptimizedImage
                    src={getFirstImageUrl(property.images)}
                    alt={property.title}
                    priority
                    aspectRatio="4/3"
                    objectFit="cover"
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  <button 
                    className="absolute top-4 right-4 w-10 h-10 bg-white rounded-full flex items-center justify-center shadow-md active:scale-90 transition-transform z-10"
                    onClick={(e) => { e.preventDefault(); /* handle fav */ }}
                  >
                    <Heart className="w-5 h-5 text-[#E8513D]" strokeWidth={2} />
                  </button>
                </div>
                
                {/* Content */}
                <div className="p-5">
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex items-center gap-1.5 text-zinc-500 max-w-[75%]">
                      <MapPin className="w-4 h-4 shrink-0" strokeWidth={2} />
                      <span className="text-[13px] truncate">{property.city}, {property.province}</span>
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      <Star className="w-4 h-4 text-zinc-900 fill-zinc-900 dark:text-zinc-100 dark:fill-zinc-100" strokeWidth={1.5} />
                      <span className="font-bold text-[13px] text-zinc-900 dark:text-zinc-100">{property.average_rating ? property.average_rating.toFixed(1) : (t('property.new') || "Novo")}</span>
                    </div>
                  </div>
                  
                  <h3 className="font-bold text-[16px] text-zinc-900 line-clamp-1 mb-2">
                    {property.title}
                  </h3>
                  
                  <div className="flex items-center gap-2 text-zinc-500 text-[13px] mb-4">
                    <span className="flex items-center gap-1">
                      {property.bedrooms || 0} {t('property.bedroomsShort') || "quartos"}
                    </span>
                    <span>·</span>
                    <span className="flex items-center gap-1">
                      {property.bathrooms || 0} {t('property.bathroomsShort') || "WC"}
                    </span>
                    <span>·</span>
                    <span className="flex items-center gap-1">
                      {property.max_guests || 0} {t('property.guestsShort') || "hósp."}
                    </span>
                  </div>
                  
                  <div className="flex items-baseline gap-1">
                    <span className="font-bold text-[18px] text-zinc-900 dark:text-zinc-100">
                      {fmt(property.price_per_night)}
                    </span>
                    <span className="text-[13px] text-zinc-500 font-medium">{t('property.perNight') || "/noite"}</span>
                  </div>
                </div>
              </Link>
            ))
          )}
        </div>
      </main>
    </div>
  );
};

export default MobileHomeView;
